/**
 * The bar above the canvas — `docs/property-maps-v2.md` §5.3, board 2.
 *
 * 56px, white, under the tab strip: the map's name with a caret (the switcher,
 * §5.6), its layer count, the sharing summary sentence, then the right-hand
 * buttons. Those come in as `actions` rather than being written here, because
 * which ones there are depends on the state: **Sharing** always, **Print /
 * PDF** once §9.6's print page exists, and **Done** only in a measure-only
 * session — the one state that has something to leave.
 *
 * The sentence is here rather than in the panel on purpose: it answers "who is
 * going to see what I am about to draw", which is a question somebody has
 * before they start rather than after they open a panel.
 */
import type { ReactNode } from 'react'
import { cn } from '../../lib/cn.js'
import { Button } from '../../components/primitives.js'
import { IconChevronDown, IconChevronLeft, IconLink, IconMaps } from '../../frame/icons.js'
import { layerWords } from '../words.js'

export function MapBar({
  name, layerCount, summary, onSwitch, onBack, actions,
}: {
  name: string
  layerCount: number
  /** One short sentence about how this map is shared, from the layers. */
  summary: string
  onSwitch?: () => void
  /**
   * The way out, where there is somewhere to go back to — a phone opens a map
   * over its own tab, so it needs one and a desktop does not.
   */
  onBack?: () => void
  actions?: ReactNode
}) {
  return (
    <div className={cn(
      'flex h-14 shrink-0 items-center gap-3 border-b border-line bg-surface px-4 sm:px-6',
    )}
    >
      {onBack && (
        <button
          type="button"
          onClick={onBack}
          className={cn(
            'inline-flex h-9 shrink-0 items-center gap-1 rounded-button-toolbar px-1.5',
            'text-cell font-semibold text-text-muted hover:bg-hover',
          )}
        >
          <IconChevronLeft size={16} />
          All maps
        </button>
      )}
      {/*
        * The one thing in the bar allowed to give way. A map's name is as long
        * as somebody typed it — "Meadowlark Homeowners Association — entrance
        * map" is 440px — and with every child `shrink-0` an iPad in portrait
        * pushed **Layers** and **Sharing** off the right-hand edge: the two
        * controls the narrow layout exists to give you. So the name truncates
        * and the buttons stay, which is the same rule the toolbar's filter
        * chips follow — what gets clipped is never the control.
        */}
      <button
        type="button"
        onClick={onSwitch}
        disabled={!onSwitch}
        className={cn(
          'inline-flex h-9 min-w-0 items-center gap-[9px] rounded-button-toolbar border',
          'border-line bg-surface px-2.5 text-[13.5px] font-bold',
          onSwitch ? 'hover:bg-hover' : 'cursor-default',
        )}
      >
        <span className="inline-flex shrink-0 text-accent"><IconMaps size={16} /></span>
        <span className="truncate">{name}</span>
        <span className="shrink-0 text-[12px] font-medium text-text-muted">
          {layerWords(layerCount)}
        </span>
        {onSwitch && (
          <span className="inline-flex shrink-0 text-text-faint"><IconChevronDown size={13} /></span>
        )}
      </button>

      <span className="hidden truncate text-cell text-text-muted md:inline">{summary}</span>

      <span className="ml-auto flex shrink-0 items-center gap-2">{actions}</span>
    </div>
  )
}

/** The Sharing button, so the bar and the panel footer draw the same one. */
export function SharingButton({ onClick }: { onClick?: () => void }) {
  return (
    <Button size="toolbar" onClick={onClick} disabled={!onClick}>
      <IconLink size={15} />
      Sharing
    </Button>
  )
}
